import { useState, useCallback, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';
import { 
  LoginFormData, 
  RegisterFormData, 
  AuthState
} from '../types/auth';

// Define AuthError class here to avoid circular imports
class AuthError extends Error {
  public code?: string;
  public field?: string;

  constructor(message: string, code?: string, field?: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code ?? undefined;
    this.field = field ?? undefined;
  }
}

export const useAuth = () => {
  const { 
    user, 
    token, 
    refreshToken,
    isAuthenticated, 
    isLoading: storeLoading,
    login: setUserAndToken,
    logout: clearUserAndToken,
    setLoading: setStoreLoading 
  } = useAuthStore();

  const [authState, setAuthState] = useState<AuthState>({
    isLoading: false,
    error: null
  });

  // Initialize auth state from stored tokens on mount
  useEffect(() => {
    const initializeAuth = async () => {
      if (authService.isAuthenticated() && !user) {
        const userFromToken = authService.getUserFromToken();
        const accessToken = authService.getAccessToken();
        
        if (userFromToken && accessToken) {
          setUserAndToken(userFromToken, accessToken);
        }
      }
    };

    initializeAuth();
  }, [user, setUserAndToken]);

  const setError = useCallback((error: AuthError | null) => {
    setAuthState(prev => ({ ...prev, error }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setAuthState(prev => ({ ...prev, isLoading: loading }));
    setStoreLoading(loading);
  }, [setStoreLoading]);

  const login = useCallback(async (credentials: LoginFormData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const authData = await authService.login(credentials);
      
      setUserAndToken(authData.user, authData.accessToken, authData.refreshToken);
    } catch (error) {
      const authError = parseAuthError(error);
      setError(authError);
      throw authError;
    } finally {
      setLoading(false);
    }
  }, [setUserAndToken, setError, setLoading]);

  const register = useCallback(async (userData: RegisterFormData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Validate password confirmation
      if (userData.password !== userData.confirmPassword) {
        throw new AuthError('Passwords do not match', 'PASSWORD_MISMATCH', 'confirmPassword');
      }

      // Send separate name fields directly to backend
      const { confirmPassword, ...registerData } = userData;
      void confirmPassword; // Explicitly mark as intentionally unused

      // Registration now returns RegistrationResponse, not AuthSuccessResponse
      // No tokens are provided since user needs admin verification
      await authService.register(registerData);
      
      // Don't set any user tokens - registration is successful but user is not logged in
      // The calling component will handle navigation to success page
    } catch (error) {
      const authError = parseAuthError(error);
      setError(authError);
      throw authError;
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await authService.logout();
      clearUserAndToken();
    } catch (error) {
      // Even if server logout fails, we still clear local state
      // Log error for debugging but don't show to user
      if (import.meta.env.MODE === 'development') {
        console.error('Logout error:', error);
      }
      clearUserAndToken();
    } finally {
      setLoading(false);
    }
  }, [clearUserAndToken, setError, setLoading]);

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      const newToken = await authService.refreshAccessToken();
      
      if (newToken && user) {
        setUserAndToken(user, newToken, refreshToken ?? undefined);
        return true;
      }
      
      // If refresh fails, logout user
      clearUserAndToken();
      return false;
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.error('Token refresh failed:', error);
      }
      clearUserAndToken();
      return false;
    }
  }, [user, refreshToken, setUserAndToken, clearUserAndToken]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  const clearAllAuthData = useCallback(() => {
    authService.clearAllAuthData();
    clearUserAndToken();
  }, [clearUserAndToken]);

  return {
    // Auth state
    user,
    token,
    refreshToken,
    isAuthenticated,
    isLoading: storeLoading || authState.isLoading,
    error: authState.error,

    // Auth actions
    login,
    register,
    logout,
    refreshAccessToken,
    clearError,
    clearAllAuthData,

    // Helper methods
    isTokenExpired: (token?: string) => {
      const tokenToCheck = token || authService.getAccessToken();
      return tokenToCheck ? authService.isTokenExpired(tokenToCheck) : true;
    },
    
    getUserFromToken: () => authService.getUserFromToken(),
  };
};

// Helper function to parse different types of errors into AuthError
function parseAuthError(error: unknown): AuthError {
  if (error instanceof AuthError) {
    return error;
  }

  if (error instanceof Error) {
    return new AuthError(error.message);
  }

  if (typeof error === 'string') {
    return new AuthError(error);
  }

  // Handle axios/API errors
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object'
  ) {
    const response = error.response as { 
      data?: { 
        error?: { 
          message?: string; 
          code?: string; 
          field?: string 
        }; 
        message?: string 
      }; 
      status?: number; 
      statusText?: string 
    };
    
    // Handle API error responses
    if (response.data?.error) {
      return new AuthError(
        response.data.error.message || 'Authentication failed',
        response.data.error.code,
        response.data.error.field
      );
    }
    
    // Handle HTTP status errors
    if (response.status === 401) {
      return new AuthError('Invalid credentials', 'INVALID_CREDENTIALS');
    }

    if (response.status === 403) {
      return new AuthError('Access forbidden', 'ACCESS_FORBIDDEN');
    }

    if (response.status === 429) {
      return new AuthError('Too many requests. Please try again later.', 'RATE_LIMITED');
    }

    if (response.status && response.status >= 500) {
      return new AuthError('Server error. Please try again later.', 'SERVER_ERROR');
    }
  }

  return new AuthError('An unexpected error occurred');
}